/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommentArrowLeft16Regular } from '@fluentui/react-icons';
import { ActionButton, BaseButton, Button, IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DragAndDropList } from 'src/core/components/DragAndDropList';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { QnaMakerFollowUpPromptModal } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerFollowUpPromptModal';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaPrompt } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';

type QnaMakerQnaPairFollowUpPromptsSectionProps = {
    selectedQnaId: number;
    isContextOnly: boolean;
    prompts: QnaMakerQnaPrompt[];
    qnaPairsJson: QnaMakerQnaSwagger[];
    handleEditPrompts: (buttonPlace: string, section: string, action: string) => void;
    handleSubmitPrompts: (newData: Partial<QnaMakerQnaSwagger>, qnaId?: number) => void;
};

const messages = defineMessages({
    followUpPrompts: {
        id: 'qnaMaker.qnaMakerQnaPairFollowUpPromptsSection.followUpPrompts',
        defaultMessage: 'Follow up prompts'
    },
    description: {
        id: 'qnaMaker.qnaMakerQnaPairFollowUpPromptsSection.description',
        defaultMessage:
            'Use follow up prompts to connect question answer pairs for multi-turn conversation. This connection allows the client application to provide a top answer and provides more questions for the user to select if needed. You can view all the connections to a question answer pair by selecting view context tree.'
    },
    addFollowUpPrompt: {
        id: 'qnaMaker.qnaMakerQnaPairFollowUpPromptsSection.addFollowUpPrompt',
        defaultMessage: 'Add follow up prompt'
    },
    textPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaPairFollowUpPromptsSection.textPlaceholder',
        defaultMessage: 'Add prompt and press esc'
    },
    edit: {
        id: 'qnaMaker.qnaMakerQnaPairFollowUpPromptsSection.edit',
        defaultMessage: 'Edit'
    },
    delete: {
        id: 'qnaMaker.qnaMakerQnaPairFollowUpPromptsSection.delete',
        defaultMessage: 'Delete'
    },
    collapse: {
        id: 'qnaMaker.qnaMakerQnaPairFollowUpPromptsSection.collapse',
        defaultMessage: 'Collapse follow up prompts section button'
    },
    expand: {
        id: 'qnaMaker.qnaMakerQnaPairFollowUpPromptsSection.expand',
        defaultMessage: 'Expand follow up prompts section button'
    }
});

const theme = getScopedTheme('QnaMakerQnaPairDetails');

const addButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ label: { color: theme.buttonColor } });

export const QnaMakerQnaPairFollowUpPromptsSection = observer((props: QnaMakerQnaPairFollowUpPromptsSectionProps) => {
    const { prompts = [], isContextOnly, handleSubmitPrompts, handleEditPrompts, selectedQnaId, qnaPairsJson } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { increaseNumberOfClicks } = useQnaMetricActions();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const [areFollowUpPromptsExpanded, setAreFollowUpPromptsExpanded] = React.useState(false);
    const [isInDragMode, setIsInDragMode] = React.useState(false);

    const [isPromptModalHidden, setIsPromptModalHidden] = React.useState(true);
    const [promptToBeEditedId, setPromptToBeEditedId] = React.useState(null);

    const hiddenQnaPairIds = [...prompts.map(p => p.qnaId), selectedQnaId, ...qnaPairsJson.filter(p => p.internalRef).map(p => p.id)];

    const handleModifyingPrompts = (newPrompts: QnaMakerQnaPrompt[], linkedPair?: Partial<QnaMakerQnaSwagger>) => {
        if (linkedPair) {
            handleSubmitPrompts({ ...linkedPair }, linkedPair.id);
        }
        handleSubmitPrompts({
            dialog: {
                isContextOnly,
                prompts: newPrompts
            }
        });
    };

    const reorderItem = (
        item: QnaMakerQnaPrompt,
        e: React.KeyboardEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement | BaseButton | Button>
    ): void => {
        const newItems = [...prompts];

        if (e.key === LuisKeyCodes.Space) {
            setIsInDragMode(!isInDragMode);
        } else if (isInDragMode && e.key === LuisKeyCodes.ArrowUp && prompts.indexOf(item) !== 0) {
            e.preventDefault();
            const idx = prompts.indexOf(item);
            newItems[idx] = prompts[idx - 1];
            newItems[idx - 1] = item;
            (document.querySelector(`#button${idx - 1}`) as HTMLElement)?.focus();
        } else if (isInDragMode && e.key === LuisKeyCodes.ArrowDown && prompts.indexOf(item) !== prompts.length - 1) {
            e.preventDefault();
            const idx = prompts.indexOf(item);
            newItems[idx] = prompts[idx + 1];
            newItems[idx + 1] = item;
            (document.querySelector(`#button${idx + 1}`) as HTMLElement)?.focus();
        }

        handleModifyingPrompts(newItems.map((p, i) => ({ ...p, displayOrder: i })));
    };

    const onPromptEdit = (qnaId: number, section: string) => {
        if (canWrite) {
            setPromptToBeEditedId(qnaId);
            setIsPromptModalHidden(false);
            handleEditPrompts('FollowupPrompts', section, 'Edit');
        }
    };

    const columns: IColumn[] = [
        {
            key: 'drag',
            minWidth: 20,
            maxWidth: 20,
            name: '',
            onRender: (item, idx) => (
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="dragFollowUpPromptButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <IconButton
                            id={`button${idx}`}
                            ariaLabel={`button${idx}`}
                            iconProps={{
                                iconName: 'DragObject',
                                styles: { root: { cursor: 'move', color: isInDragMode ? 'grey' : null } }
                            }}
                            onKeyDown={e => reorderItem(item, e)}
                        />
                    }
                />
            )
        },
        {
            key: 'items',
            minWidth: 200,
            maxWidth: 200,
            name: '',
            onRender: item => (
                <Stack onClick={() => onPromptEdit(item.qnaId, 'ClickToEdit')}>
                    <TruncatedText
                        maxWidth={200}
                        styles={{ root: { cursor: 'text', display: 'flex', paddingTop: '5px', marginRight: '5px' } }}>
                        {item.displayText}
                    </TruncatedText>
                </Stack>
            )
        },
        {
            key: 'edit',
            minWidth: 20,
            maxWidth: 20,
            name: '',
            onRender: item => (
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="editFollowUpPromptButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <IconButton
                            disabled={!canWrite}
                            title={localize(messages.edit)}
                            ariaLabel={localize(messages.edit)}
                            iconProps={{
                                iconName: 'Edit',
                                styles: { root: { cursor: 'pointer' } }
                            }}
                            onClick={() => onPromptEdit(item.qnaId, 'Button')}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            minWidth: 20,
            maxWidth: 20,
            name: '',
            onRender: item => (
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteFollowUpPromptButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <IconButton
                            disabled={!canWrite}
                            title={localize(messages.delete)}
                            ariaLabel={localize(messages.delete)}
                            iconProps={{
                                iconName: 'Delete',
                                styles: { root: { cursor: 'pointer' } }
                            }}
                            onClick={() => {
                                handleModifyingPrompts(prompts.filter((_, idx) => prompts.indexOf(item) !== idx));
                                handleEditPrompts('FollowupPrompts', 'Button', 'Delete');
                            }}
                        />
                    }
                />
            )
        }
    ];

    const handleFollowUpPromptSubmit = ({ prompt, linkedPair }: { prompt: QnaMakerQnaPrompt; linkedPair: Partial<QnaMakerQnaSwagger> }) => {
        setIsPromptModalHidden(true);

        if (promptToBeEditedId) {
            setPromptToBeEditedId(null);
            handleModifyingPrompts(
                prompts.map(p => (p.qnaId === promptToBeEditedId ? prompt : p)),
                linkedPair
            );

            return;
        }

        const newDisplayedOrder = Math.max(...prompts.map(p => p.displayOrder)) + 1;
        handleModifyingPrompts([...prompts, { ...prompt, displayOrder: newDisplayedOrder }], linkedPair);
    };

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack horizontal tokens={{ childrenGap: 5 }}>
                <FontIcon
                    tabIndex={0}
                    aria-hidden={false}
                    aria-label={areFollowUpPromptsExpanded ? localize(messages.collapse) : localize(messages.expand)}
                    onClick={() => setAreFollowUpPromptsExpanded(!areFollowUpPromptsExpanded)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            setAreFollowUpPromptsExpanded(!areFollowUpPromptsExpanded);
                        }
                    }}
                    iconName={areFollowUpPromptsExpanded ? 'ChevronDown' : 'ChevronRight'}
                    style={{ cursor: 'pointer' }}
                />
                <CommentArrowLeft16Regular />
                <LuisText fontWeight={600}>{localize(messages.followUpPrompts)}</LuisText>
                <LuisText fontWeight={600}>({prompts.length})</LuisText>
            </Stack>
            {areFollowUpPromptsExpanded && (
                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText variant="smallPlus" color={theme.descriptionColor}>
                        {localize(messages.description)}
                    </LuisText>
                    <RBACTooltip
                        hidden={canWrite}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="addFollowUpPromptButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <ActionButton
                                disabled={!canWrite}
                                styles={addButtonStyles}
                                iconProps={{ iconName: 'Add' }}
                                text={localize(messages.addFollowUpPrompt)}
                                onClick={() => {
                                    increaseNumberOfClicks();
                                    setIsPromptModalHidden(false);
                                    handleEditPrompts('FollowupPrompts', 'Button', 'Add');
                                }}
                            />
                        }
                    />
                    <DragAndDropList
                        items={[...prompts]}
                        listColumns={columns}
                        onRenderItem={undefined}
                        canDragAndDrop={canWrite}
                        onEditItems={updatedPrompts =>
                            handleSubmitPrompts({
                                dialog: {
                                    isContextOnly,
                                    prompts: updatedPrompts.map((p, i) => ({ ...p, displayOrder: i }))
                                }
                            })
                        }
                    />
                </Stack>
            )}

            {!isPromptModalHidden && (
                <QnaMakerFollowUpPromptModal
                    qnaPairsJson={qnaPairsJson}
                    hidden={isPromptModalHidden}
                    isContextOnly={isContextOnly}
                    selectedQnaId={selectedQnaId}
                    hiddenQnaPairIds={hiddenQnaPairIds}
                    onSubmit={handleFollowUpPromptSubmit}
                    prompt={prompts.find(p => p.qnaId === promptToBeEditedId)}
                    onDismiss={() => {
                        setIsPromptModalHidden(true);
                        setPromptToBeEditedId(null);
                    }}
                />
            )}
        </Stack>
    );
});
